import {
  Button,
  Input,
  InputNumber,
  message,
  Pagination,
  Popconfirm,
  Space,
  Table,
  Tabs,
  Tag,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import moment from "moment";
import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  deleteManualInventory,
  fetchVendorInventory,
  syncZohoInventory,
  updateManualInventory,
} from "../../apis/inventory.api";
import AddManualInventoryModal from "../../components/inventory/add_manual_inventory_modal";
import IncomingStockTable from "../../components/inventory/incoming_stock_table";
import StockPurchaseOrdersTable from "../../components/inventory/stock_purchase_orders_table";
import { IVendorInventory } from "../../interfaces/inventory.interface";
import { usePermissions } from "../../custom_hooks/permission/permission_hook";

const formatDate = (date?: string | null) => {
  const parsed = moment(date);
  return date && parsed.isValid() ? parsed.format("DD MMM YYYY, HH:mm") : "-";
};

export default function Inventory() {
  const { hasPermission } = usePermissions();
  const [data, setData] = useState<IVendorInventory[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [syncing, setSyncing] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editQty, setEditQty] = useState<number | null>(null);
  // ?tab=incoming / ?tab=pos open those tabs directly, so a view can be linked.
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get("tab");
  const activeTab = tabParam === "incoming" || tabParam === "pos" ? tabParam : "stock";
  // Anyone who can open this page (inventory.read) may manage manual rows.
  const canManage = true;
  const pageSize = 10;

  const getData = useCallback(
    async (searchTerm: string = "", pageNum: number = 1) => {
      setLoading(true);
      const response = await fetchVendorInventory({
        page: pageNum,
        page_size: pageSize,
        search: searchTerm,
      });
      if (response) {
        setData(response.results);
        setTotalItems(response.count);
      }
      setLoading(false);
    },
    []
  );

  useEffect(() => {
    getData(search, page);
  }, []);

  const handleSearch = () => {
    setPage(1);
    getData(search, 1);
  };

  const handleSync = async () => {
    if (!hasPermission("sync.trigger")) {
      message.error("You don't have permission to sync inventory");
      return;
    }
    setSyncing(true);
    const result = await syncZohoInventory();
    setSyncing(false);
    if (result) {
      getData(search, page);
    }
  };

  const saveQuantity = async (record: IVendorInventory) => {
    if (editQty === null || editQty < 0) {
      message.error("Enter a quantity of zero or more");
      return;
    }
    const updated = await updateManualInventory(record.id, editQty);
    if (updated) {
      setEditingId(null);
      getData(search, page);
    }
  };

  const handleDelete = async (record: IVendorInventory) => {
    if (await deleteManualInventory(record.id)) {
      getData(search, page);
    }
  };

  const columns: ColumnsType<IVendorInventory> = [
    {
      title: "Variant ID",
      dataIndex: "variant_id",
      key: "variant_id",
      width: 100,
      fixed: "left",
    },
    {
      title: "Variant Name",
      dataIndex: "variant_name",
      key: "variant_name",
      width: 300,
      fixed: "left",
    },
    {
      title: "Variant Handle",
      dataIndex: "variant_handle",
      key: "variant_handle",
      width: 250,
    },
    {
      title: "Vendor Name",
      dataIndex: "vendor_name",
      key: "vendor_name",
      width: 250,
    },
    {
      title: "Warehouse",
      dataIndex: "warehouse_name",
      key: "warehouse_name",
      width: 200,
    },
    {
      title: "Godown Name",
      dataIndex: "vendor_godown_name",
      key: "vendor_godown_name",
      width: 180,
    },
    {
      title: "Quantity",
      dataIndex: "quantity",
      key: "quantity",
      width: 140,
      render: (quantity: string, record: IVendorInventory) =>
        editingId === record.id ? (
          <InputNumber
            min={0}
            precision={2}
            size="small"
            value={editQty}
            onChange={(v) => setEditQty(v)}
            onPressEnter={() => saveQuantity(record)}
            autoFocus
          />
        ) : (
          <Tag color={Number(quantity) > 0 ? "green" : "red"}>
            {Number(quantity).toFixed(2)}
          </Tag>
        ),
    },
    {
      title: "Source",
      dataIndex: "is_manual",
      key: "is_manual",
      width: 100,
      render: (isManual: boolean) =>
        isManual ? <Tag color="purple">Manual</Tag> : <Tag>Synced</Tag>,
    },
    {
      title: "Batch No",
      dataIndex: "batch_no",
      key: "batch_no",
      width: 120,
      render: (batch_no: string | null) => batch_no || "-",
    },
    {
      title: "Last Updated",
      key: "last_updated",
      width: 180,
      // The Zoho sync (the only active inventory writer) stamps `last_synced_at`;
      // `last_updated_in_vendor_warehouse` is only filled by the disabled
      // sheet/Tally paths, so it is null on every Zoho row.
      render: (_: unknown, record: IVendorInventory) =>
        formatDate(record.last_updated_in_vendor_warehouse ?? record.last_synced_at),
    },
    {
      title: "Created At",
      dataIndex: "created_at",
      key: "created_at",
      width: 180,
      render: (date: string | null) => formatDate(date),
    },
    ...(canManage
      ? [
          {
            title: "Actions",
            key: "actions",
            width: 160,
            fixed: "right" as const,
            // Synced rows belong to the sync and would be overwritten, so
            // only manual rows can be edited or deleted here.
            render: (_: unknown, record: IVendorInventory) => {
              if (!record.is_manual) return "-";
              if (editingId === record.id) {
                return (
                  <Space>
                    <Button size="small" type="primary" onClick={() => saveQuantity(record)}>
                      Save
                    </Button>
                    <Button size="small" onClick={() => setEditingId(null)}>
                      Cancel
                    </Button>
                  </Space>
                );
              }
              return (
                <Space>
                  <Button
                    size="small"
                    onClick={() => {
                      setEditingId(record.id);
                      setEditQty(Number(record.quantity));
                    }}
                  >
                    Edit
                  </Button>
                  <Popconfirm
                    title="Delete this inventory row?"
                    description="Sync won't bring it back."
                    onConfirm={() => handleDelete(record)}
                    okText="Delete"
                    okButtonProps={{ danger: true }}
                    cancelText="Cancel"
                  >
                    <Button size="small" danger>
                      Delete
                    </Button>
                  </Popconfirm>
                </Space>
              );
            },
          },
        ]
      : []),
  ];

  const stockTab = (
    <>
      <div className="d-flex justify-content-end align-items-center pb-3">
        <div className="d-flex gap-2">
          <Input.Search
            value={search}
            onChange={(ev) => setSearch(ev.target.value)}
            onPressEnter={handleSearch}
            onSearch={handleSearch}
            style={{ width: 300 }}
            placeholder="Search by variant name, vendor, warehouse..."
            allowClear
          />
          {canManage && (
            <Button onClick={() => setAddOpen(true)}>Add Inventory</Button>
          )}
          <Popconfirm
            title="Sync Inventory"
            description="Are you sure you want to sync the inventory from Zoho?"
            onConfirm={handleSync}
            okText="Yes"
            cancelText="No"
          >
            <Button
              type="primary"
              loading={syncing}
            >
              Sync Inventory
            </Button>
          </Popconfirm>
        </div>
      </div>
      <Table
        dataSource={data}
        loading={loading}
        pagination={false}
        columns={columns}
        rowKey="id"
        scroll={{ x: 2100 }}
      />
      <AddManualInventoryModal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onAdded={() => {
          setPage(1);
          getData(search, 1);
        }}
      />
      <Pagination
        current={page}
        total={totalItems}
        showSizeChanger={false}
        pageSize={pageSize}
        className="mt-3"
        onChange={(p) => {
          setPage(p);
          getData(search, p);
        }}
        showTotal={(total, range) =>
          `${range[0]}-${range[1]} of ${total} items`
        }
      />
    </>
  );

  return (
    <div className="mx-3 my-3 px-3 py-2 white-color-bg">
      <h3 className="mb-0">Inventory</h3>
      <Tabs
        activeKey={activeTab}
        onChange={(key) => setSearchParams(key === "stock" ? {} : { tab: key })}
        destroyInactiveTabPane={false}
        items={[
          { key: "stock", label: "Vendor Inventory", children: stockTab },
          {
            key: "incoming",
            label: "Incoming Stock (Open POs)",
            children: <IncomingStockTable />,
          },
          {
            key: "pos",
            label: "Stock Purchase Orders",
            children: <StockPurchaseOrdersTable />,
          },
        ]}
      />
    </div>
  );
}
